import { Modifier, Placement } from "@popperjs/core";
import clsx from "clsx";
import React, {
  CSSProperties,
  ReactNode,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { usePopper } from "react-popper";

import { isRecord } from "@tsmono/util";

interface PopOverProps {
  id: string;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  positionEl: HTMLElement | null;
  placement?: Placement;
  showArrow?: boolean;
  offset?: [number, number];
  usePortal?: boolean;
  hoverDelay?: number;
  closeOnMouseLeave?: boolean;

  className?: string | string[];
  arrowClassName?: string | string[];

  children: ReactNode;
  styles?: CSSProperties;
}

/**
 * A controlled Popper component for displaying content relative to a reference element
 */
export const PopOver: React.FC<PopOverProps> = ({
  id,
  isOpen,
  setIsOpen,
  positionEl,
  children,
  placement = "bottom",
  showArrow = true,
  offset = [0, 8],
  className = "",
  arrowClassName = "",
  usePortal = true,
  hoverDelay = 250,
  closeOnMouseLeave = true,
  styles = {},
}) => {
  const popperRef = useRef<HTMLDivElement | null>(null);
  const arrowRef = useRef<HTMLDivElement | null>(null);
  const [portalContainer, setPortalContainer] = useState<HTMLElement | null>(
    null
  );

  // For delayed hover functionality
  const [shouldShowPopover, setShouldShowPopover] = useState(false);
  const hoverTimerRef = useRef<number | null>(null);
  const isMouseMovingRef = useRef(false);
  const isOverPopoverRef = useRef(false);
  const dismissalTimerRef = useRef<number | null>(null);

  // Stable ref for setIsOpen to avoid re-running effects when the callback identity changes
  const setIsOpenRef = useRef(setIsOpen);
  // eslint-disable-next-line react-hooks/refs -- latest-callback ref pattern; rule doesn't model this
  setIsOpenRef.current = setIsOpen;

  // Setup hover timer and mouse movement detection
  // eslint-disable-next-line tsmono/no-raw-use-effect -- baselined at rule introduction; migrate to a named hook or derived state
  useEffect(() => {
    const handleMouseMove = () => {
      isMouseMovingRef.current = true;

      // Clear any existing timer when mouse moves
      if (hoverTimerRef.current !== null) {
        window.clearTimeout(hoverTimerRef.current);
      }

      // Start a new timer to check if mouse has stopped moving
      hoverTimerRef.current = window.setTimeout(() => {
        if (isOpen) {
          isMouseMovingRef.current = false;
          setShouldShowPopover(true);
        }
      }, hoverDelay);
    };

    const handleMouseLeave = () => {
      if (hoverTimerRef.current !== null) {
        window.clearTimeout(hoverTimerRef.current);
      }
      isMouseMovingRef.current = false;

      // Add a delay before dismissing to allow user to move mouse to popover
      if (dismissalTimerRef.current !== null) {
        window.clearTimeout(dismissalTimerRef.current);
      }
      dismissalTimerRef.current = window.setTimeout(() => {
        if (!isOverPopoverRef.current) {
          setShouldShowPopover(false);
          setIsOpenRef.current(false);
        }
      }, 300);
    };

    const handleMouseDown = (event: MouseEvent) => {
      // Only cancel popover on mouse down outside the popover content
      const target = event.target instanceof Node ? event.target : null;
      if (popperRef.current && !popperRef.current.contains(target)) {
        if (hoverTimerRef.current !== null) {
          window.clearTimeout(hoverTimerRef.current);
        }
        setShouldShowPopover(false);
        setIsOpenRef.current(false);
      }
    };

    if (!isOpen || hoverDelay <= 0) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- conditional sync of external prop, not cascading
      setShouldShowPopover(isOpen);

      // Track whether mousedown originated inside the popover or on the
      // trigger element. We use capture phase to detect this BEFORE the
      // event bubbles to portaled children.
      let mouseDownInsidePopover = false;
      let mouseDownOnTrigger = false;

      const captureListener = (event: MouseEvent) => {
        const target = event.target instanceof Node ? event.target : null;
        mouseDownInsidePopover = popperRef.current?.contains(target) ?? false;
        // A click on the trigger element should NOT close via this handler —
        // the trigger's own onClick will toggle the popover. Closing here
        // then reopening in the trigger handler would net to no change.
        mouseDownOnTrigger = positionEl?.contains(target) ?? false;
      };

      const bubbleListener = () => {
        if (!popperRef.current) return;
        if (mouseDownInsidePopover || mouseDownOnTrigger) return;
        setIsOpenRef.current(false);
      };

      // Capture phase fires first, before any children (including portaled ones)
      document.addEventListener("mousedown", captureListener, true);
      // Bubble phase fires after - by then we know if it started inside
      document.addEventListener("mousedown", bubbleListener);

      return () => {
        document.removeEventListener("mousedown", captureListener, true);
        document.removeEventListener("mousedown", bubbleListener);
      };
    }

    // Add event listeners to the positionEl (the trigger element)
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (positionEl && isOpen) {
      positionEl.addEventListener("mousemove", handleMouseMove);
      positionEl.addEventListener("mouseleave", handleMouseLeave);

      // Add document-wide mousedown listener to dismiss on interaction outside popover
      document.addEventListener("mousedown", handleMouseDown);

      // Initial mouse move to start the timer
      handleMouseMove();
    } else {
      setShouldShowPopover(false);
    }

    return () => {
      if (positionEl) {
        positionEl.removeEventListener("mousemove", handleMouseMove);
        positionEl.removeEventListener("mouseleave", handleMouseLeave);
      }

      // Clean up the document mousedown listener
      document.removeEventListener("mousedown", handleMouseDown);

      // Clean up all timers to prevent leaks
      if (hoverTimerRef.current !== null) {
        window.clearTimeout(hoverTimerRef.current);
        hoverTimerRef.current = null;
      }
      if (dismissalTimerRef.current !== null) {
        window.clearTimeout(dismissalTimerRef.current);
        dismissalTimerRef.current = null;
      }
    };
  }, [isOpen, positionEl, hoverDelay]);

  // Effect to create portal container when needed
  // eslint-disable-next-line tsmono/no-raw-use-effect -- baselined at rule introduction; migrate to a named hook or derived state
  useEffect(() => {
    // Only create portal when the popover is open
    if (usePortal && isOpen && shouldShowPopover) {
      let container = document.getElementById(id);

      if (!container) {
        container = document.createElement("div");
        container.id = id;
        container.style.position = "absolute";
        container.style.top = "0";
        container.style.left = "0";
        container.style.zIndex = "9999";
        container.style.width = "0";
        container.style.height = "0";
        container.style.overflow = "visible";

        document.body.appendChild(container);
      }

      // eslint-disable-next-line react-hooks/set-state-in-effect -- syncing React with externally-created DOM node
      setPortalContainer(container);

      return () => {
        // Clean up only when unmounting or when the popover closes
        if (document.body.contains(container)) {
          document.body.removeChild(container);
          setPortalContainer(null);
        }
      };
    }

    return undefined;
  }, [usePortal, isOpen, shouldShowPopover, id]);

  // Popper modifier pair that caps the popover to the full viewport
  // (minus padding), not to whatever the popover currently happens to be.
  // Bounding the size up-front lets popper shift it freely to fit, avoiding
  // the "text unwraps → popper chases a moving width" race while still
  // giving the popover as much room as the viewport allows.
  //
  // The popper v2 community "maxSize" formula (width - overflow[side] - x)
  // collapses to the popper's current width once preventOverflow has shifted
  // it inside, so we derive the cap directly from the viewport — accurate
  // for a body-portal popover and placement-agnostic.
  const maxSizeModifier = React.useMemo<
    Modifier<"maxSize", { padding: number }>
  >(
    () => ({
      name: "maxSize",
      enabled: true,
      phase: "main",
      requiresIfExists: ["offset", "preventOverflow", "flip"],
      options: { padding: 8 },
      fn({ state, name, options }) {
        const padding =
          typeof options.padding === "number" ? options.padding : 8;
        state.modifiersData[name] = {
          width: Math.max(0, window.innerWidth - 2 * padding),
          height: Math.max(0, window.innerHeight - 2 * padding),
        };
      },
    }),
    []
  );

  const applyMaxSizeModifier = React.useMemo<Modifier<"applyMaxSize", object>>(
    () => ({
      name: "applyMaxSize",
      enabled: true,
      phase: "beforeWrite",
      requires: ["maxSize"],
      fn({ state }) {
        const data: unknown = state.modifiersData["maxSize"];
        if (
          !isRecord(data) ||
          typeof data["width"] !== "number" ||
          typeof data["height"] !== "number"
        ) {
          return;
        }
        state.styles.popper = {
          ...state.styles.popper,
          maxWidth: `${data["width"]}px`,
          maxHeight: `${data["height"]}px`,
        };
      },
    }),
    []
  );

  // Configure modifiers for popper
  const modifiers = [
    { name: "offset", options: { offset } },
    { name: "preventOverflow", options: { padding: 8 } },
    {
      name: "arrow",
      enabled: showArrow,
      options: {
        // Latent — see meridianlabs-ai/ts-mono#90. `arrowRef.current` is
        // null on the first render, so the arrow modifier initially has
        // no element. The force-update useEffect below schedules a
        // popper recompute after refs attach, and the `positionedThisOpen`
        // gating hides the popover (visibility: hidden, offscreen) until
        // popper has positioned it for this open cycle — so the
        // unpositioned-arrow render never paints. Migrating to a
        // state-backed `arrowElement` is the textbook fix but the
        // existing belt-and-suspenders gating means there is no
        // user-visible symptom today; deferring until the broader
        // popper-ref migration.
        // eslint-disable-next-line react-hooks/refs
        element: arrowRef.current,
        padding: 5, // This keeps the arrow from getting too close to the corner
      },
    },
    {
      name: "computeStyles",
      options: {
        gpuAcceleration: false,
        adaptive: true,
      },
    },
    // Ensure popper is positioned correctly with respect to its reference element
    {
      name: "flip",
      options: {
        fallbackPlacements: ["top", "right", "bottom", "left"],
      },
    },
    maxSizeModifier,
    applyMaxSizeModifier,
  ];

  // Use popper hook with modifiers
  //
  // Latent — see meridianlabs-ai/ts-mono#90. `popperRef.current` is null on
  // the first render, so usePopper initially has no popper element to
  // position. The force-update useEffect below recomputes once refs
  // attach, and `positionedThisOpen` keeps the popover hidden until
  // popper has positioned it for this open cycle. No user-visible
  // symptom today; migration to state-backed `popperElement` is the
  // mechanically clean fix but deferred until we touch this component
  // for other reasons.
  const {
    styles: popperStyles,
    attributes,
    state,
    update,
    forceUpdate,
    // eslint-disable-next-line react-hooks/refs
  } = usePopper(positionEl, popperRef.current, {
    placement,
    strategy: "fixed",
    modifiers,
  });

  // Per-open-cycle "has popper positioned this open?" signal. react-popper's
  // internal useState (for styles/attributes) persists across open/close
  // cycles, so on the first render of a reopen the last open's top/left are
  // still in popperStyles — we'd paint at stale coordinates for one frame.
  //
  // Derived (not stored) from popper's `state` identity: we snapshot `state`
  // at the open transition via a ref and flip positionedThisOpen true once
  // popper hands us a *different* state object for this cycle. The re-render
  // that carries the fresh state is driven by react-popper's own setState
  // inside its layout effect — that runs before the browser's first paint
  // after open, so the first paint lands with the popover at its final
  // position. No setState-in-effect, no offscreen frame, no flash.
  const [prevIsOpen, setPrevIsOpen] = useState(isOpen);
  const [openBaselineState, setOpenBaselineState] =
    useState<typeof state>(state);
  if (prevIsOpen !== isOpen) {
    setPrevIsOpen(isOpen);
    if (isOpen) {
      setOpenBaselineState(state);
    }
  }
  const positionedThisOpen =
    isOpen && state !== null && state !== openBaselineState;

  // Force update when needed refs change.
  //
  // This effect papers over the stale-null reads of `popperRef.current` /
  // `arrowRef.current` passed to usePopper above (see meridianlabs-ai/ts-mono#90).
  // A 10ms setTimeout gives the callback refs time to attach, then we ask
  // popper to recompute against the now-populated DOM nodes. Including
  // `arrowRef.current` in the dep array is intentional: re-running this
  // effect when the arrow node attaches is exactly what makes the
  // workaround load-bearing. Reading `.current` during render to derive
  // the dep is the reason for the `react-hooks/refs` suppression, and
  // the lint rule can't see that the read drives effect scheduling
  // (hence `exhaustive-deps`). Removing this effect requires the
  // state-backed migration prescribed by #90.
  // eslint-disable-next-line tsmono/no-raw-use-effect -- baselined at rule introduction; migrate to a named hook or derived state
  useEffect(() => {
    if (update && isOpen && shouldShowPopover) {
      const timer = setTimeout(() => {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        update();
      }, 10);
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps, react-hooks/refs
  }, [update, isOpen, shouldShowPopover, showArrow, arrowRef.current]);

  // Pre-paint correction. popper's first computed `state` (pass 1) comes from
  // its synchronous, transform-blind offset math; when the reference sits in a
  // CSS-transformed subtree (e.g. a rotated grid header) that pass is wrong and
  // popper only corrects on a later `getBoundingClientRect`-based pass. The
  // post-paint `setTimeout(update)` above does that correction one frame too
  // late — the wrong position paints first, then jumps. Forcing the recompute
  // here (a layout effect, so it runs after refs attach but before the browser
  // paints) lands the corrected position on the first painted frame. Gated to
  // fire once per open, on the render where popper first hands us a state.
  const didPrePosition = useRef(false);
  // eslint-disable-next-line tsmono/no-raw-use-effect -- baselined at rule introduction; migrate to a named hook or derived state
  useLayoutEffect(() => {
    if (!isOpen || !shouldShowPopover) {
      didPrePosition.current = false;
      return;
    }
    if (forceUpdate && state && !didPrePosition.current) {
      didPrePosition.current = true;
      forceUpdate();
    }
  }, [isOpen, shouldShowPopover, forceUpdate, state]);

  // When the popover is shown and positioned, track mouse enter/leave on the popover itself
  // and use that to block dismissal while hovering over the popover
  // eslint-disable-next-line tsmono/no-raw-use-effect -- baselined at rule introduction; migrate to a named hook or derived state
  useEffect(() => {
    // Wait for both the popover to be visible AND Popper to have calculated positioning
    if (
      !popperRef.current ||
      !isOpen ||
      !shouldShowPopover ||
      !state?.placement
    ) {
      return;
    }

    const popperEl = popperRef.current;

    const handlePopoverMouseEnter = () => {
      isOverPopoverRef.current = true;
      // Cancel any pending dismissal when mouse enters popover
      if (dismissalTimerRef.current !== null) {
        window.clearTimeout(dismissalTimerRef.current);
        dismissalTimerRef.current = null;
      }
      // Also cancel the hover delay timer
      if (hoverTimerRef.current !== null) {
        window.clearTimeout(hoverTimerRef.current);
      }
      // Ensure popover stays visible
      setShouldShowPopover(true);
    };

    const handlePopoverMouseLeave = (e: MouseEvent) => {
      // Only dismiss if we're actually leaving the popover container
      // Check if the related target is still within the popover
      if (
        e.relatedTarget instanceof Node &&
        popperEl.contains(e.relatedTarget)
      ) {
        return;
      }
      if (!closeOnMouseLeave) {
        return;
      }
      isOverPopoverRef.current = false;
      // Dismiss when leaving the popover
      setShouldShowPopover(false);
      setIsOpenRef.current(false);
    };

    // Use capture phase to ensure we catch events before children
    popperEl.addEventListener("mouseenter", handlePopoverMouseEnter, true);
    popperEl.addEventListener("mouseleave", handlePopoverMouseLeave, true);

    return () => {
      popperEl.removeEventListener("mouseenter", handlePopoverMouseEnter, true);
      popperEl.removeEventListener("mouseleave", handlePopoverMouseLeave, true);
    };
  }, [isOpen, shouldShowPopover, state?.placement, closeOnMouseLeave]);

  // Define arrow data-* attribute based on placement
  const getArrowDataPlacement = () => {
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (!state || !state.placement) return placement;
    return state.placement;
  };

  // Get the actual placement from Popper state
  const actualPlacement = state?.placement || placement;

  // For a CSS triangle, we use the border trick
  // A CSS triangle doesn't need separate border styling like a rotated square would

  // Popper container styles
  const defaultPopperStyles: CSSProperties = {
    backgroundColor: "var(--bs-body-bg)",
    padding: "12px",
    borderRadius: "var(--bs-border-radius)",
    boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
    border: "solid 1px var(--bs-border-color)",
    zIndex: 1200,
    position: "relative",
    // Apply opacity transition to smooth the appearance
    opacity: state?.placement ? 1 : 0,
    transition: "opacity 0.1s",
    maxWidth: "80%",
    maxHeight: "80%",
    overflowY: "hidden",
  };

  // Early return if not open or should not show due to hover delay
  if (!isOpen || (hoverDelay > 0 && !shouldShowPopover)) {
    return null;
  }

  // For position-aware rendering. Use the per-open-cycle flag rather than
  // react-popper state, which persists styles across open/close cycles and
  // would otherwise cause paints at stale or placeholder coordinates on the
  // first render of any open.
  const popperReady = positionedThisOpen;
  const currentPopperStyle = popperStyles.popper ?? {};
  const currentArrowStyle = popperStyles.arrow ?? {};
  const positionedStyle: CSSProperties = popperReady
    ? {
        ...currentPopperStyle,
        opacity: 1,
        visibility: "visible" as const,
      }
    : {
        ...currentPopperStyle,
        // visibility: hidden is bulletproof — no paint regardless of what
        // position values are in popperStyles.popper (which may include
        // stale coordinates from a prior open since react-popper's useState
        // persists, or placeholder 0/0 on a brand-new instance).
        visibility: "hidden" as const,
        opacity: 0,
        position: "fixed" as const,
        top: "-9999px",
        left: "-9999px",
      };

  // Create the popper content with position-aware styles
  const popperContent = (
    <div
      ref={popperRef}
      style={{ ...defaultPopperStyles, ...positionedStyle, ...styles }}
      className={clsx(className)}
      {...attributes.popper}
    >
      {children}

      {showArrow && (
        <>
          {/* Invisible div for Popper.js to use as reference */}
          <div
            ref={arrowRef}
            style={{ position: "absolute", visibility: "hidden" }}
            data-placement={getArrowDataPlacement()}
          />

          {/* Arrow container - positioned by Popper */}
          <div
            className={clsx("popper-arrow-container", arrowClassName)}
            style={{
              ...currentArrowStyle,
              position: "absolute",
              zIndex: 1,
              // Size and positioning based on placement - smaller arrow
              ...(actualPlacement.startsWith("top") && {
                bottom: "-8px",
                width: "16px",
                height: "8px",
              }),
              ...(actualPlacement.startsWith("bottom") && {
                top: "-8px",
                width: "16px",
                height: "8px",
              }),
              ...(actualPlacement.startsWith("left") && {
                right: "-8px",
                width: "8px",
                height: "16px",
              }),
              ...(actualPlacement.startsWith("right") && {
                left: "-8px",
                width: "8px",
                height: "16px",
              }),
              // Content positioning
              overflow: "hidden",
            }}
          >
            {/* Border element (rendered behind) */}
            {actualPlacement.startsWith("top") && (
              <div
                style={{
                  position: "absolute",
                  width: 0,
                  height: 0,
                  borderStyle: "solid",
                  borderWidth: "8px 8px 0 8px",
                  borderColor: "#eee transparent transparent transparent",
                  top: "0px",
                  left: "0px",
                }}
              />
            )}
            {actualPlacement.startsWith("bottom") && (
              <div
                style={{
                  position: "absolute",
                  width: 0,
                  height: 0,
                  borderStyle: "solid",
                  borderWidth: "0 8px 8px 8px",
                  borderColor: "transparent transparent #eee transparent",
                  top: "0px",
                  left: "0px",
                }}
              />
            )}
            {actualPlacement.startsWith("left") && (
              <div
                style={{
                  position: "absolute",
                  width: 0,
                  height: 0,
                  borderStyle: "solid",
                  borderWidth: "8px 0 8px 8px",
                  borderColor: "transparent transparent transparent #eee",
                  top: "0px",
                  left: "0px",
                }}
              />
            )}
            {actualPlacement.startsWith("right") && (
              <div
                style={{
                  position: "absolute",
                  width: 0,
                  height: 0,
                  borderStyle: "solid",
                  borderWidth: "8px 8px 8px 0",
                  borderColor: "transparent #eee transparent transparent",
                  top: "0px",
                  left: "0px",
                }}
              />
            )}

            {/* Actual triangle created with CSS borders, slightly smaller and offset to create border effect */}
            <div
              style={{
                position: "absolute",
                width: 0,
                height: 0,
                borderStyle: "solid",
                backgroundColor: "transparent",
                // Position relative to border triangle
                left: "0px",
                zIndex: 1,

                // Top placement - pointing down
                ...(actualPlacement.startsWith("top") && {
                  borderWidth: "7px 7px 0 7px",
                  borderColor: "white transparent transparent transparent",
                  top: "0px",
                }),

                // Bottom placement - pointing up
                ...(actualPlacement.startsWith("bottom") && {
                  borderWidth: "0 7px 7px 7px",
                  borderColor: "transparent transparent white transparent",
                  top: "1px",
                }),

                // Left placement - pointing right
                ...(actualPlacement.startsWith("left") && {
                  borderWidth: "7px 0 7px 7px",
                  borderColor: "transparent transparent transparent white",
                  left: "0px",
                }),

                // Right placement - pointing left
                ...(actualPlacement.startsWith("right") && {
                  borderWidth: "7px 7px 7px 0",
                  borderColor: "transparent white transparent transparent",
                  left: "1px",
                }),
              }}
            />
          </div>
        </>
      )}
    </div>
  );

  // If using portal and the container exists, render through the portal
  if (usePortal && portalContainer) {
    return createPortal(popperContent, portalContainer);
  }

  // Otherwise render normally
  return popperContent;
};
